import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './BugReport.css';
import { ClipLoader } from 'react-spinners'; // React spinner
import api from '../axiosConfig';
import { handleEmailSubmission } from './utils/emailService';

const BugReport = () => {
    const maxDescriptionLength = 500; // Max character limit for description

    // Initialize state from localStorage or empty values
    const [bugType, setBugType] = useState(() => localStorage.getItem('bugType') || '');
    const [description, setDescription] = useState(() => localStorage.getItem('description') || '');
    const [stepsToReproduce, setStepsToReproduce] = useState(() => localStorage.getItem('stepsToReproduce') || '');
    const [characterCount, setCharacterCount] = useState(description.length);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userDetails, setUserDetails] = useState({ firstName: '', lastName: '', email: '' });
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    // Fetch user details when the component mounts
    useEffect(() => {
        const fetchUserDetails = async () => {
            const token = localStorage.getItem('token');
            try {
                const response = await api.get('/userdata', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.data.length > 0) {
                    const userData = response.data[0];
                    setUserDetails({
                        firstName: userData.firstName || '',
                        lastName: userData.lastName || '',
                        email: userData.email || '',
                    });
                }
            } catch (error) {
                console.error('Error fetching user details:', error);
                setErrorMessage('Failed to fetch user details. Please log in again.');
                navigate('/');
            }
        };
        fetchUserDetails();
    }, [navigate]);

    // Persist form data to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('bugType', bugType);
        localStorage.setItem('description', description);
        localStorage.setItem('stepsToReproduce', stepsToReproduce);
    }, [bugType, description, stepsToReproduce]);

    // Handle description field changes
    const handleDescriptionChange = (e) => {
        const value = e.target.value;
        if (value.length <= maxDescriptionLength) {
            setDescription(value);
            setCharacterCount(value.length);
            setErrorMessage(''); // Clear any previous error messages
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrorMessage(''); // Clear any previous error messages

        const payload = {
            bugType,
            description,
            stepsToReproduce,
            firstName: userDetails.firstName,
            lastName: userDetails.lastName,
            email: userDetails.email,
        };

        const { success, message } = await handleEmailSubmission('/send-email', payload);

        if (success) {
            alert('Your bug report has been submitted successfully!');
            // Clear form fields and localStorage after successful submission
            setBugType('');
            setDescription('');
            setStepsToReproduce('');
            setCharacterCount(0);
            localStorage.removeItem('bugType');
            localStorage.removeItem('description');
            localStorage.removeItem('stepsToReproduce');
        } else {
            setErrorMessage(message);
        }

        setIsSubmitting(false);
    };

    return (
        <div className="bug-report-container">
            <h2>Bug Report</h2>
            {errorMessage && <p className="error-message">{errorMessage}</p>}
            <form onSubmit={handleSubmit} className="bug-report-form">
                <label htmlFor="bugType">Bug Type:</label>
                <select
                    id="bugType"
                    value={bugType}
                    onChange={(e) => setBugType(e.target.value)}
                    required
                >
                    <option value="" disabled>
                        Select Bug Type
                    </option>
                    <option value="UI/UX Issue">UI/UX Issue</option>
                    <option value="Functionality Error">Functionality Error</option>
                    <option value="Performance Issue">Performance Issue</option>
                    <option value="Other">Other</option>
                </select>

                <label htmlFor="description">Bug Description:</label>
                <textarea
                    id="description"
                    value={description}
                    onChange={handleDescriptionChange}
                    rows="6"
                    required
                    placeholder="Briefly describe the issue (max 500 characters)"
                ></textarea>
                <p className="character-count">
                    {characterCount}/{maxDescriptionLength} characters
                </p>

                <label htmlFor="stepsToReproduce">Steps to Reproduce:</label>
                <textarea
                    id="stepsToReproduce"
                    value={stepsToReproduce}
                    onChange={(e) => setStepsToReproduce(e.target.value)}
                    rows="6"
                    required
                    placeholder="Describe step-by-step how to reproduce the bug"
                ></textarea>

                <button
                    type="submit"
                    className="submit-button"
                    disabled={isSubmitting || !bugType || !description || !stepsToReproduce}
                >
                    {isSubmitting ? <ClipLoader size={20} color="#ffffff" /> : 'Submit Bug Report'}
                </button>
            </form>
        </div>
    );
};

export default BugReport;
